import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { SITE } from "../config/site";
import { LANGS } from "../i18n";

const OG_LOCALE = { tr: "tr_TR", en: "en_US", ru: "ru_RU" };

/**
 * Per-page SEO: title, meta description, canonical, hreflang alternates,
 * Open Graph / Twitter tags and optional JSON-LD structured data.
 */
export default function SEO({ title, description, image, type = "website", jsonLd, noindex = false }) {
  const { i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || "tr";
  const { pathname } = useLocation();
  const path = pathname === "/" ? "" : pathname;
  const canonical = `${SITE.url}${path}`;
  const fullTitle = title ? `${title}` : SITE.name;
  const img = image ? (image.startsWith("http") ? image : SITE.url + image) : `${SITE.url}/img/logo.png`;

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={canonical} />

      {/* hreflang alternates (language via ?lng=) */}
      {LANGS.map((l) => (
        <link
          key={l.code}
          rel="alternate"
          hrefLang={l.code}
          href={`${canonical}${path ? "" : "/"}?lng=${l.code}`.replace("/?", "/?")}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={canonical || SITE.url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={img} />
      <meta property="og:locale" content={OG_LOCALE[lang] || "tr_TR"} />
      {LANGS.filter((l) => l.code !== lang).map((l) => (
        <meta key={l.code} property="og:locale:alternate" content={OG_LOCALE[l.code]} />
      ))}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={img} />

      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
}
